import type { CommonRes } from ".";
import { api } from ".";

export interface UserSignUpPayload {
  email: string;
  password: string;
  /** 닉네임 */
  nickname: string;
  /** 신상품 및 기획전 출시 알림 */
  newProductAgreed?: boolean;
  /** 광고 및 이벤트 할인 이메일 */
  adAgreed?: boolean;
  /** 개인 맞춤 상품 추천 알림 */
  recommendAgreed?: boolean;
}

export interface VerifyEmailCodePayload {
  email: string;
  code: string;
}

export interface VerifyPhoneCodePayload {
  /** 전화번호 (국제 코드 포함) */
  phone: string;
  /** 코드 (6자리) */
  code: string;
}

export interface PostNicknameValidatePayload {
  /** 닉네임 */
  nickname: string;
}

export interface UserLoginPayload {
  email: string;
  password: string;
}

export interface UserLoginResponse {
  token: string;
  refreshToken: string;
}

export interface UserOneTimeTokenResponse {
  oneTimeToken: string;
}

export interface PasswordEmailVerifyResponse {
  /** 비밀번호 변경 시 Authorization 헤더로 사용하는 one time token */
  token: string;
}

export interface PatchPasswordPayload {
  password: string;
  /** 비밀번호 찾기 검증으로 발급된 one time token */
  token: string;
}

export interface PostSnsLoginPayload {
  /** SNS 로그인 SDK(Google Sign-In / LINE Login·LIFF)에서 발급받은 idToken */
  idToken: string;
}

export interface PostSnsLoginResponse {
  /** SNS 계정 연결 확인이 필요한지 여부. true면 email/linkToken이 내려가고, 연결 확인 모달 후 provider별 link API 호출 필요 */
  needsLinkConfirm: boolean;
  /** 연결 확인 또는 신규 가입이 필요한 경우 표시용 SNS 계정 이메일 */
  email?: string;
  /** 연결 확인이 필요한 경우 provider별 link API에 전달할 단기 JWT (5분 만료) */
  linkToken?: string;
  /** 가입된 이메일이 없어 SNS 회원가입이 필요한지 여부 */
  needsSignup?: boolean;
  /** SNS 회원가입이 필요한 경우 provider별 signup API에 전달할 단기 JWT (10분 만료) */
  signupToken?: string;
  /** 신규 가입 시 닉네임 입력칸 기본값으로 쓰는 SNS 표시 이름. profile scope 미동의 시 내려오지 않는다 */
  name?: string;
  /** provider가 이메일을 주지 않아(동의 화면에서 거부) 이메일을 직접 입력받아야 하는지 여부. true면 emailToken이 내려가고, 이메일 입력 화면에서 email/code → email/verify 순으로 호출 필요 */
  needsEmail?: boolean;
  /** 이메일 직접 입력이 필요한 경우 code/verify API에 전달할 단기 JWT (10분 만료). SNS 계정 식별자(sub)를 담고 있다 */
  emailToken?: string;
  /** 이미 연결된 계정인 경우 발급되는 access token */
  token?: string;
  /** 이미 연결된 계정인 경우 발급되는 refresh token */
  refreshToken?: string;
}

export interface PostSnsLinkPayload {
  /** SNS login 응답으로 받은 단기 linkToken */
  linkToken: string;
}

export interface PostSnsSignupPayload {
  /** SNS login 응답으로 받은 단기 signupToken */
  signupToken: string;
  /** 닉네임 */
  nickname: string;
  /** 신상품 및 기획전 출시 알림 */
  newProductAgreed?: boolean;
  /** 광고 및 이벤트 할인 이메일 */
  adAgreed?: boolean;
  /** 개인 맞춤 상품 추천 알림 */
  recommendAgreed?: boolean;
}

/** Google / LINE 3-step 플로우의 요청·응답 shape은 동일하다 — provider별 alias */
export type PostGoogleLoginPayload = PostSnsLoginPayload;
export type PostGoogleLoginResponse = PostSnsLoginResponse;
export type PostGoogleLinkPayload = PostSnsLinkPayload;
export type PostGoogleSignupPayload = PostSnsSignupPayload;

export type PostLineLoginPayload = PostSnsLoginPayload;
export type PostLineLoginResponse = PostSnsLoginResponse;
export type PostLineLinkPayload = PostSnsLinkPayload;
export type PostLineSignupPayload = PostSnsSignupPayload;

export interface PostLineEmailCodePayload {
  /** LINE login 응답으로 받은 단기 emailToken */
  emailToken: string;
  /** 사용자가 직접 입력한 이메일 */
  email: string;
}

export interface PostLineEmailVerifyPayload extends PostLineEmailCodePayload {
  /** 메일로 받은 6자리 인증 코드 */
  code: string;
}

/**
 * @description 유저 회원가입 (응답: 204 No Content)
 */
export const postUserSignUp = (data: UserSignUpPayload) =>
  api.post("user/auth/signup", { json: data });

/**
 * @description 유저 로그인
 */
export const postUserLogin = (data: UserLoginPayload) =>
  api
    .post("user/auth/login", {
      json: data,
    })
    .json<CommonRes<UserLoginResponse>>();

/**
 * @description one time jwt token 재발급 (access_token 필요)
 */
export const getUserOneTimeToken = () =>
  api
    .get("user/auth/one-time-token")
    .json<CommonRes<UserOneTimeTokenResponse>>();

/**
 * @description 이메일 인증번호 발송
 */
export const postEmailCode = (email: string) =>
  api
    .post("auth/email/code", {
      json: { email },
    })
    .json<{ success: boolean }>();

/**
 * @description 회원 가입용 이메일 인증 코드 발송 (응답 없음 / 409: 이미 가입된 이메일)
 */
export const postUserEmailCode = (email: string) =>
  api.post("user/auth/email/code", {
    json: { email },
  });

/**
 * @description 닉네임 중복 검사 (응답 없음 / 409: 이미 존재하는 닉네임)
 */
export const postNicknameValidate = (data: PostNicknameValidatePayload) =>
  api.post("user/auth/nickname/validate", {
    json: data,
  });

/**
 * @description 이메일 인증번호 검증
 */
export const verifyEmailCode = ({ email, code }: VerifyEmailCodePayload) =>
  api
    .post("auth/email/verify", {
      json: { email, code },
    })
    .json<{ success: boolean }>();

/**
 * @description 회원 가입용 휴대폰 인증 코드 발송 (응답 없음 / 409: 이미 가입된 휴대폰)
 */
export const postUserPhoneCode = (phone: string) =>
  api.post("user/auth/phone/code", {
    json: { phone },
  });

/**
 * @description 휴대폰 인증번호 검증
 */
export const verifyPhoneCode = ({ phone, code }: VerifyPhoneCodePayload) =>
  api.post("auth/phone/verify", {
    json: { phone, code },
  });

/**
 * @description 회원 가입용 휴대폰 인증 코드 발송 (응답 없음 / 409: 이미 가입된 휴대폰)
 */
export const postSignupPhoneCode = (phone: string) =>
  api.post("user/auth/signup/phone/code", {
    json: { phone },
  });

/**
 * @description 회원 가입용 휴대폰 인증 코드 검증 (응답 없음 / 401: 인증 코드 만료 또는 불일치)
 */
export const postSignupPhoneVerify = ({
  phone,
  code,
}: VerifyPhoneCodePayload) =>
  api.post("user/auth/signup/phone/verify", {
    json: { phone, code },
  });

/**
 * @description 회원 정보 수정용 휴대폰 인증 코드 발송 (access_token 필요 / 409: 이미 가입된 휴대폰)
 */
export const postInfoPhoneCode = (phone: string) =>
  api.post("user/auth/info/phone/code", {
    json: { phone },
  });

/**
 * @description 회원 정보 수정용 휴대폰 인증 코드 검증 (access_token 필요 / 401: 인증 코드 만료 또는 불일치)
 */
export const postInfoPhoneVerify = ({ phone, code }: VerifyPhoneCodePayload) =>
  api.post("user/auth/info/phone/verify", {
    json: { phone, code },
  });

/**
 * @description 비밀번호 찾기용 휴대폰 인증 코드 발송 (응답 없음 / 404: 존재하지 않는 휴대폰)
 */
export const postPasswordPhoneCode = (phone: string) =>
  api.post("user/auth/password/phone/code", {
    json: { phone },
  });

/**
 * @description 비밀번호 찾기용 휴대폰 인증 코드 검증 (one time token 반환 / 401: 인증 코드 만료 또는 불일치)
 */
export const postPasswordPhoneVerify = (data: VerifyPhoneCodePayload) =>
  api
    .post("user/auth/password/phone/verify", {
      json: data,
    })
    .json<CommonRes<PasswordEmailVerifyResponse>>();

/**
 * @description 비밀번호 찾기 이메일 인증 코드 발송
 */
export const postPasswordEmailCode = (email: string) =>
  api.post("user/auth/password/email/code", {
    json: { email },
  });

/**
 * @description 비밀번호 찾기 이메일 인증 코드 검증 (one time token 반환)
 */
export const postPasswordEmailVerify = (data: VerifyEmailCodePayload) =>
  api
    .post("user/auth/password/email/verify", {
      json: data,
    })
    .json<CommonRes<PasswordEmailVerifyResponse>>();

/**
 * @description 비밀번호 변경 (검증 단계에서 발급된 one time token 필요)
 */
export const patchPassword = ({ password, token }: PatchPasswordPayload) =>
  api.patch("user/auth/password", {
    json: { password },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

/**
 * @description Google 로그인 / 연결확인 / 신규가입 분기 (1단계). idToken 검증 후 토큰 또는 linkToken/signupToken 발급
 */
export const postGoogleLogin = (data: PostGoogleLoginPayload) =>
  api
    .post("user/auth/google/login", {
      json: data,
    })
    .json<CommonRes<PostGoogleLoginResponse>>();

/**
 * @description Google 계정 연결 (2-A단계). 기존 계정에 Google 계정을 연결하고 access/refresh 토큰 발급
 */
export const postGoogleLink = (data: PostGoogleLinkPayload) =>
  api
    .post("user/auth/google/link", {
      json: data,
    })
    .json<CommonRes<UserLoginResponse>>();

/**
 * @description Google SNS 회원가입 (2-B단계). signupToken + 닉네임/약관동의로 신규 가입 후 access/refresh 토큰 발급
 */
export const postGoogleSignup = (data: PostGoogleSignupPayload) =>
  api
    .post("user/auth/google/signup", {
      json: data,
    })
    .json<CommonRes<UserLoginResponse>>();

/**
 * @description LINE 로그인 / 이메일입력 / 연결확인 / 신규가입 분기 (1단계). idToken 검증 후 토큰 또는 emailToken/linkToken/signupToken 발급 (401: 유효하지 않은 idToken)
 */
export const postLineLogin = (data: PostLineLoginPayload) =>
  api
    .post("user/auth/line/login", {
      json: data,
    })
    .json<CommonRes<PostLineLoginResponse>>();

/**
 * @description LINE 이메일 직접 입력용 인증 코드 발송 (1-B단계). LINE이 이메일을 주지 않은 경우 사용. 회원가입용 email/code 와 달리 이미 가입된 이메일이어도 409를 내지 않는다. 코드는 5분간 유효 (응답 없음 / 401: emailToken 만료·변조 / 500: 메일 발송 실패)
 */
export const postLineEmailCode = (data: PostLineEmailCodePayload) =>
  api.post("user/auth/line/email/code", {
    json: data,
  });

/**
 * @description LINE 이메일 직접 입력용 인증 코드 검증 (1-B단계). 검증 성공 시 login 과 동일한 shape으로 연결확인(linkToken) / 신규가입(signupToken) 분기 응답 (401: emailToken 만료·변조 또는 인증 코드 만료·불일치)
 */
export const postLineEmailVerify = (data: PostLineEmailVerifyPayload) =>
  api
    .post("user/auth/line/email/verify", {
      json: data,
    })
    .json<CommonRes<PostLineLoginResponse>>();

/**
 * @description LINE 계정 연결 (2-A단계). 기존 계정에 LINE 계정을 연결하고 access/refresh 토큰 발급 (401: linkToken 만료·변조 / 409: 이미 다른 계정에 연결된 LINE 계정)
 */
export const postLineLink = (data: PostLineLinkPayload) =>
  api
    .post("user/auth/line/link", {
      json: data,
    })
    .json<CommonRes<UserLoginResponse>>();

/**
 * @description LINE SNS 회원가입 (2-B단계). signupToken + 닉네임/약관동의로 신규 가입 후 access/refresh 토큰 발급 (401: signupToken 만료·변조 / 409: 이미 존재하는 닉네임 또는 가입된 이메일)
 */
export const postLineSignup = (data: PostLineSignupPayload) =>
  api
    .post("user/auth/line/signup", {
      json: data,
    })
    .json<CommonRes<UserLoginResponse>>();
